// library imports
import { useEffect, useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  DatePicker,
  Flex,
  Table,
  Button,
  Divider,
  Tooltip,
  Skeleton,
} from "antd";
import {
  DownloadOutlined,
  InfoCircleOutlined,
  CaretLeftOutlined,
  CaretRightOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
// style imports
import "../../styles/SavingReport/SavingReport.css";
// component imports
import CustomGroupedColumnChart from "../../components/common/CustomGroupedColumnChart";
import SavingReportOverallSummary from "../../components/saving_report_microgrid/SavingReportOverallSummary";
// hook imports
import useFetch from "../../hooks/useFetch";
// context imports
import { IndexContext } from "../../context/IndexContext";
// config imports
import {
  LIGHT_BLUE,
  DARK_GREEN,
  MICROGRID_SITE_IDS,
} from "../../configs/configs";

const { RangePicker } = DatePicker;
const { Column, ColumnGroup } = Table;

const COST_SAVING_TYPES = ["Before AIE-DR", "After AIE-DR"];

function fillMonthData(data, month) {
  const daysInMonth = month.daysInMonth();
  const dataMap = {};
  data.forEach((d) => {
    dataMap[`${d.date}__${d.type}`] = d.value;
  });

  const result = [];
  for (let day = 1; day <= daysInMonth; day++) {
    const date = month.date(day).format("YYYY-MM-DD");
    COST_SAVING_TYPES.forEach((type) => {
      result.push({
        date,
        type,
        value: dataMap[`${date}__${type}`] ?? null,
      });
    });
  }
  return result;
}

export default function SavingReportPage() {
  // fetch wrapper
  const [fetchWrapper] = useFetch();
  const navigate = useNavigate();

  // load index context
  const { siteSelected, siteList, notificationApi, setDisabledSiteSelection } =
    useContext(IndexContext);

  useEffect(
    () => {
      if (siteSelected && !MICROGRID_SITE_IDS.includes(siteSelected)) {
        navigate("/Reports/SavingReport", { replace: true });
      }
    }, // eslint-disable-next-line react-hooks/exhaustive-deps
    [siteSelected],
  );

  // loading states
  const [downloadButtonLoading, setDownloadButtonLoading] = useState(false);
  const [
    loadingSavingReportOverallSummary,
    setLoadingSavingReportOverallSummary,
  ] = useState(false);
  const [loadingMonthSummaryTable, setLoadingMonthSummaryTable] =
    useState(false);
  const [loadingDailySavingDataTable, setLoadingDailySavingDataTable] =
    useState(false);
  const [loadingDailySavingChart, setLoadingDailySavingChart] = useState(false);

  // overall saving report summary table data
  const [
    overallSavingReportSummaryTableData,
    setOverallSavingReportSummaryTableData,
  ] = useState([]);
  // overall saving report summary chart data
  const [
    overallSavingReportSummaryChartData,
    setOverallSavingReportSummaryChartData,
  ] = useState([]);

  // summary of months of the year data
  const [tableData, setTableData] = useState([]);
  const [tableColumn, setTableColumn] = useState([
    {
      title: "Saving",
      dataIndex: "Saving",
      key: "Saving",
      width: 200,
    },
  ]);

  // daily table data
  const [dailyDataTable, setDailyDataTable] = useState([]);

  // selected year
  const [year, setYear] = useState(null);

  // cost saving chart
  const [chartData, setChartData] = useState([]);
  const [chartMonth, setChartMonth] = useState(dayjs());

  // site commissioning date
  const [siteCommissioningDate, setSiteCommissioningDate] = useState(null);

  // value of RangePicker when either one of the start date and end date has been selected
  const [dates, setDates] = useState(null);

  // value of RangePicker if both the start date and end date has been selected
  const [value, setValue] = useState(null);

  // committed search params — only updated when Search is clicked
  // shape: { year, value, searchCount }
  const [searchParams, setSearchParams] = useState(null);
  const [isSearchClicked, setIsSearchClicked] = useState(false);

  // Disable months that are not in the selected year
  const disabledDate = (current) => {
    if (!year) return true; // Disable all if year is not selected yet
    return current.year() !== year.year();
  };

  const onOpenChange = (open) => {
    if (open) {
      setDates([null, null]); // Reset dates to [null, null] when the picker is opened
    } else {
      setDates(null); // Clear the state completely when the picker is closed
    }
  };

  const onCalendarChange = (val) => {
    if (val[1] && !val[0]) {
      // If end date is selected first, set the start date to the Jan of the year
      const sameMonthStart = val[1].clone().startOf("month");
      setDates([sameMonthStart, val[1]]);
    } else {
      setDates(val); // Otherwise, just set the selected dates
    }
  };

  /* API to get site commissioning date */
  function getSiteCommissioningDate() {
    fetchWrapper({
      endpoint_url: "common/getSiteCommissioningDate",
      method: "GET",
      set_content_type_header: false,
      params: {
        site_id: siteSelected,
      },

      onSuccess: (response) => {
        setSiteCommissioningDate(response.data.site_commissioning_date);

        // when site commissioning date changes, need to update year, dates and date values
        setYear(
          dayjs(
            Math.max(
              dayjs().startOf("year").valueOf(),
              dayjs(
                response.data.site_commissioning_date,
                "YYYY-MM-DD",
              ).valueOf(),
            ),
          ),
        );

        setDates([
          dayjs(
            Math.max(
              dayjs().startOf("year").valueOf(),
              dayjs(
                response.data.site_commissioning_date,
                "YYYY-MM-DD",
              ).valueOf(),
            ),
          ),
          dayjs(),
        ]);

        setValue([
          dayjs(
            Math.max(
              dayjs().startOf("year").valueOf(),
              dayjs(
                response.data.site_commissioning_date,
                "YYYY-MM-DD",
              ).valueOf(),
            ),
          ),
          dayjs(),
        ]);
      },
      onResponseError: (response) => {
        notificationApi.error({
          message: "Failed to get site commissioning date.",
          description: response.statusText,
        });
      },
    });
  }

  /** API to get cost daily saving table and chart data for the selected month */
  function getDailySavingData(month) {
    setLoadingDailySavingDataTable(true);
    setLoadingDailySavingChart(true);

    fetchWrapper({
      endpoint_url: "saving_report_microgrid/getDailySavingData",
      method: "GET",
      set_content_type_header: false,
      params: {
        site_id: siteSelected,
        start_date: month.startOf("month").format("YYYY-MM-DD"),
        end_date: month.endOf("month").format("YYYY-MM-DD"),
      },

      onSuccess: (response) => {
        setDailyDataTable(response.data.table_data);
        setChartData(fillMonthData(response.data.chart_data, month));
      },
      onResponseError: (response) => {
        notificationApi.error({
          message: "Failed to get daily saving data.",
          description: response.statusText,
        });
      },

      onFinish: () => {
        setLoadingDailySavingDataTable(false);
        setLoadingDailySavingChart(false);
      },
    });
  }

  /** API to get saving report summary */
  function getSavingReportSummary() {
    setLoadingMonthSummaryTable(true);

    fetchWrapper({
      endpoint_url: "saving_report_microgrid/getSavingReportSummary",
      method: "GET",
      set_content_type_header: false,
      params: {
        site_id: siteSelected,
        start_date: value[0].startOf("month").format("YYYY-MM-DD"),
        end_date: value[1].endOf("month").format("YYYY-MM-DD"),
      },

      onSuccess: (response) => {
        setTableColumn(response.data.table_column);
        setTableData(response.data.table_data);
      },
      onResponseError: (response) => {
        notificationApi.error({
          message: "Failed to get microgrid saving report summary.",
          description: response.statusText,
        });
      },

      onFinish: () => {
        setLoadingMonthSummaryTable(false);
      },
    });
  }

  /** API to download saving report */
  function downloadSavingReport() {
    setDownloadButtonLoading(true);

    fetchWrapper({
      endpoint_url: "saving_report_microgrid/downloadSavingReport",
      method: "POST",
      json_output: false,
      body: {
        site_id: siteSelected,
        year: searchParams.year.year(),
        start_date: searchParams.value[0].startOf("month").format("YYYY-MM-DD"),
        end_date: searchParams.value[1].endOf("month").format("YYYY-MM-DD"),
        saving_report_summary: JSON.stringify(tableData),
        saving_report_overall_summary_table_data: JSON.stringify(
          overallSavingReportSummaryTableData,
        ),
      },

      onSuccess: (response) => {
        if (response.headers.get("content-type") !== "application/json") {
          // no issue with the response. returned a spreadsheet
          response.blob().then((blob) => {
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            const startMonth = searchParams.value[0].startOf("month");
            const endMonth = searchParams.value[1].endOf("month");
            const isSameMonth = startMonth.isSame(endMonth, "month");
            a.download = isSameMonth
              ? `SavingReport_${startMonth.format("MMM")}_${startMonth.format("YYYY")}_${siteList.find((site) => site.value === siteSelected)?.label}.pdf`
              : `SavingReport_${startMonth.format("MMM")}_${endMonth.format("MMM")}_${startMonth.format("YYYY")}_${siteList.find((site) => site.value === siteSelected)?.label}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
          });
        }
      },
      onResponseError: (response) => {
        notificationApi.error({
          message: "Failed to download file",
        });
      },

      onFinish: () => {
        setDownloadButtonLoading(false);
      },
    });
  }

  useEffect(
    () => {
      setValue(null);
      setSearchParams(null);
      setChartMonth(dayjs());
      setOverallSavingReportSummaryTableData([]);
      setOverallSavingReportSummaryChartData([]);
      setTableData([]);
      setDailyDataTable([]);
      setChartData([]);
      setIsSearchClicked(false);
      if (siteSelected) {
        getSiteCommissioningDate();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [siteSelected],
  );

  // only fires when chartMonth changes; gated on searchParams so it doesn't
  // auto-call on initial load or after site change
  useEffect(() => {
    if (searchParams && chartMonth && siteSelected) {
      getDailySavingData(chartMonth);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartMonth]);

  function handleSearch() {
    if (!siteSelected) {
      notificationApi.error({ message: "Please select a site first." });
      return;
    }
    if (!value || !value[0] || !value[1]) {
      notificationApi.error({ message: "Please select a month range." });
      return;
    }
    const params = {
      year,
      value,
    };
    setSearchParams(params);
    setChartMonth(value[1].startOf("month"));
    setIsSearchClicked(true);
    getSavingReportSummary();
  }

  useEffect(
    () => {
      setDisabledSiteSelection(
        loadingMonthSummaryTable ||
          downloadButtonLoading ||
          loadingSavingReportOverallSummary ||
          loadingDailySavingDataTable,
      );
    }, // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      loadingMonthSummaryTable,
      downloadButtonLoading,
      loadingSavingReportOverallSummary,
      loadingDailySavingDataTable,
    ],
  );

  const isControlsDisabled =
    loadingMonthSummaryTable ||
    downloadButtonLoading ||
    loadingSavingReportOverallSummary ||
    loadingDailySavingDataTable;

  return (
    <div
      style={{
        margin: "10px",
        height: "90%",
      }}
      className="saving-report"
    >
      <Card
        styles={{
          body: {
            padding: 0,
          },
        }}
        title={
          <Flex justify="space-between" align="center" wrap={true} gap={8}>
            <Flex gap={8} align="center">
              <p>Saving Report</p>
              <Tooltip title="Click Search button after selecting year and month">
                <InfoCircleOutlined />
              </Tooltip>
            </Flex>

            <Flex gap={8}>
              <Button
                disabled={
                  loadingMonthSummaryTable ||
                  loadingSavingReportOverallSummary ||
                  searchParams == null
                }
                loading={downloadButtonLoading}
                icon={<DownloadOutlined />}
                onClick={() => {
                  if (searchParams) {
                    downloadSavingReport();
                  }
                }}
              />
            </Flex>
          </Flex>
        }
      >
        {/* Scrollable content */}
        <div
          style={{
            overflowY: "auto",
            maxHeight: "calc(100vh - 200px)",
            padding: "0 24px",
          }}
        >
          <Flex align="center" style={{ marginTop: "12px" }} gap={8}>
            <div className="mr-2">Year: </div>
            <DatePicker
              allowClear={false}
              disabled={
                siteCommissioningDate === null ||
                loadingSavingReportOverallSummary
              }
              picker="year"
              value={year}
              onChange={(value) => {
                setYear(value);

                if (siteCommissioningDate) {
                  if (value.startOf("year") < dayjs().startOf("year")) {
                    setDates([
                      dayjs(
                        Math.max(
                          value.startOf("year").valueOf(),
                          dayjs(siteCommissioningDate, "YYYY-MM-DD").valueOf(),
                        ),
                      ),
                      value.endOf("year"),
                    ]);

                    setValue([
                      dayjs(
                        Math.max(
                          value.startOf("year").valueOf(),
                          dayjs(siteCommissioningDate, "YYYY-MM-DD").valueOf(),
                        ),
                      ),
                      value.endOf("year"),
                    ]);
                  } else {
                    setDates([value.startOf("year"), dayjs()]);
                    setValue([value.startOf("year"), dayjs()]);
                  }
                }
              }}
              maxDate={dayjs()}
              minDate={dayjs(siteCommissioningDate)}
            />

            <Flex align="center">
              <div className="mr-1">Month:</div>
              <Tooltip title="You need to select year first before selecting month if you wish to view data of another year.">
                <InfoCircleOutlined className="mr-2" />
              </Tooltip>
            </Flex>
            <RangePicker
              allowClear={false}
              disabled={loadingMonthSummaryTable}
              maxDate={dayjs()}
              minDate={dayjs(siteCommissioningDate)}
              picker="month"
              defaultValue={dates}
              value={dates || value}
              disabledDate={disabledDate}
              onCalendarChange={onCalendarChange}
              onChange={(val) => {
                setValue(val);
              }}
              onOpenChange={onOpenChange}
            />
            <Button
              type="primary"
              icon={<SearchOutlined />}
              loading={loadingMonthSummaryTable}
              disabled={isControlsDisabled || !siteSelected}
              onClick={handleSearch}
              style={{ marginLeft: 8 }}
            >
              Search
            </Button>
          </Flex>

          <SavingReportOverallSummary
            isSearchClicked={isSearchClicked}
            dates={searchParams?.value}
            loading={loadingSavingReportOverallSummary}
            setLoading={setLoadingSavingReportOverallSummary}
            tableData={overallSavingReportSummaryTableData}
            setTableData={setOverallSavingReportSummaryTableData}
            chartData={overallSavingReportSummaryChartData}
            setChartData={setOverallSavingReportSummaryChartData}
          />

          <Divider style={{ borderColor: "lightgray" }} />

          <Table
            style={{ marginBottom: "12px" }}
            columns={tableColumn}
            dataSource={tableData}
            pagination={false}
            loading={loadingMonthSummaryTable}
            size="small"
          />

          <Divider style={{ borderColor: "lightgray" }} />

          <Flex
            justify="space-between"
            align="center"
            style={{ marginBottom: 8 }}
          >
            <Button
              type="text"
              icon={
                searchParams?.value &&
                chartMonth.isAfter(
                  dayjs(searchParams.value[0]).startOf("month"),
                ) ? (
                  <CaretLeftOutlined />
                ) : (
                  <></>
                )
              }
              disabled={
                loadingDailySavingChart ||
                !searchParams ||
                !chartMonth.isAfter(
                  dayjs(searchParams.value[0]).startOf("month"),
                )
              }
              onClick={() =>
                setChartMonth((prev) =>
                  prev.subtract(1, "month").startOf("month"),
                )
              }
            />
            <h3 style={{ margin: 0 }}>
              Fuel Saving
              {searchParams ? ` - ${chartMonth.format("MMMM YYYY")}` : ""}
            </h3>
            <Button
              type="text"
              icon={
                searchParams?.value &&
                chartMonth.isBefore(
                  dayjs(searchParams.value[1]).startOf("month"),
                ) ? (
                  <CaretRightOutlined />
                ) : (
                  <></>
                )
              }
              disabled={
                loadingDailySavingChart ||
                !searchParams ||
                !chartMonth.isBefore(
                  dayjs(searchParams.value[1]).startOf("month"),
                )
              }
              onClick={() =>
                setChartMonth((prev) => prev.add(1, "month").startOf("month"))
              }
            />
          </Flex>

          <Table
            loading={loadingMonthSummaryTable}
            style={{ width: "100%", marginBottom: 12 }}
            dataSource={dailyDataTable}
            bordered
            size="small"
            pagination={{
              responsive: true,
              showSizeChanger: true,
              position: "bottomRight",
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} of ${total} items`,
            }}
          >
            <Column title="Date" dataIndex="Date" key="Date" />
            <ColumnGroup title="Genset Usage">
              <Column
                title="Before AIE-DR"
                dataIndex="GensetUsageBefore"
                key="GensetUsageBefore"
              />
              <Column
                title="After AIE-DR"
                dataIndex="GensetUsageAfter"
                key="GensetUsageAfter"
              />
              <Column
                title="Saved"
                dataIndex="GensetUsageSaved"
                key="GensetUsageSaved"
              />
            </ColumnGroup>

            <ColumnGroup title="Estimated Fuel Usage">
              <Column
                title="Before AIE-DR"
                dataIndex="FuelUsageBefore"
                key="FuelUsageBefore"
              />

              <Column
                title="After AIE-DR"
                dataIndex="FuelUsageAfter"
                key="FuelUsageAfter"
              />

              <Column
                title="Saved"
                dataIndex="FuelUsageSaved"
                key="FuelUsageSaved"
              />
            </ColumnGroup>
            <ColumnGroup title="Estimated Cost">
              <Column
                title="Before AIE-DR"
                dataIndex="CostBefore"
                key="CostBefore"
              />
              <Column
                title="After AIE-DR"
                dataIndex="CostAfter"
                key="CostAfter"
              />
              <Column title="Saved" dataIndex="CostSaved" key="CostSaved" />
            </ColumnGroup>
          </Table>

          <h3>Estimated Cost Saving Chart</h3>

          <div style={{ width: "100%", height: "300px" }}>
            {loadingDailySavingChart ? (
              <Skeleton />
            ) : (
              <CustomGroupedColumnChart
                data={chartData}
                xAxis="date"
                yAxis="value"
                colorKey="type"
                xAxisTitle="Date"
                yAxisTitle="Cost (RM)"
                domain={["Before AIE-DR", "After AIE-DR"]}
                range={[LIGHT_BLUE, DARK_GREEN]}
              />
            )}
          </div>
        </div>
      </Card>
    </div>
  );
}
